import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import styledWeb from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'
import { useOutsideClickClose } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/popover/useOutsideClickClose'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { MESSENGER } from '../../../../common/constants/routingConstants'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../../../data/store'
import { setLastConversationClientId } from '../../../../data/userSettingsSlice'
import { ChatEntryPoint } from '../../../messages/BaseChat'
import { COMPACT_MESSENGER_TOP_OFFSET, COMPACT_MESSENGER_Z_INDEX } from '../../../messages/constants'
import { getSelectedConversationId } from '../../../messages/data/messagesSelectors'
import {
  clearSelectedConversation,
  setMessageCenterTarget,
  setPendingInitialThreadId,
  setSelectedConversation,
} from '../../../messages/data/messagesSlice'
import { useClientMessenger } from '../../../messages/hooks/useClientMessenger'
import useNavigateToClientAssignment from '../../../messages/hooks/useNavigateToClientAssignment'
import useNavigateToClientNote from '../../../messages/hooks/useNavigateToClientNote'
import BaseMessenger from '../../../messages/messenger/BaseMessenger'
import { CompactMessengerDragHandle } from '../../../messages/messenger/CompactMessengerDragHandle'

const Panel = styledWeb.dialog(({ theme }) => ({
  position: 'fixed',
  margin: 0,
  padding: 0,
  left: 'auto',
  boxSizing: 'border-box',
  top: 'min(200px, 24vh)',
  right: theme.spacing['16px'],
  width: 'min(376px, calc(100vw - 32px))',
  height: 'min(500px, calc(76vh - 16px))',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  border: `1px solid ${theme.colors.borderDefault}`,
  borderRadius: theme.spacing['12px'],
  boxShadow: `0 2px 26px ${theme.colors.shadowLow}`,
  overflow: 'hidden',
  zIndex: COMPACT_MESSENGER_Z_INDEX,
  '@supports (height: 100dvh)': {
    top: COMPACT_MESSENGER_TOP_OFFSET,
    height: 'min(500px, calc(76dvh - 16px))',
  },
  '@media (max-height: 600px)': {
    top: theme.spacing['16px'],
    height: 'calc(100vh - 32px)',
    '@supports (height: 100dvh)': {
      height: 'calc(100dvh - 32px)',
    },
  },
}))
const MessengerContainer = styled.View({ flex: 1 })
const noop = () => {}

export const ProviderPairMessagePanel = ({
  clientId,
  clientName,
  counterpart,
  onClose,
  onUnavailable,
  testIDPrefix,
}: {
  clientId: string
  clientName: string
  counterpart: CareHistoryProvider
  onClose: () => void
  onUnavailable: () => void
  testIDPrefix: string
}) => {
  const { formatMessage } = useIntl()
  const isActiveMessenger = useClientMessenger(onClose)
  const theme = useTheme()
  const panelRef = useRef<HTMLDialogElement>(null)
  const onMouseDownCapture = useOutsideClickClose(panelRef, undefined, true, onClose)
  const providerId = useSelector(getAuthUserId)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const navigateToAssignment = useNavigateToClientAssignment()
  const navigateToNote = useNavigateToClientNote()
  const [isReady, setIsReady] = useState(false)
  const selectedConversationId = useSelector(getSelectedConversationId)
  const previousConversationId = useRef<string | null>(null)
  const panelSelection = useRef<string | null>(null)
  const ownsSelection = useRef(false)

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const panel = panelRef.current
    panel?.focus({ preventScroll: true })
    return () => {
      // Outside clicks can move focus elsewhere; only restore focus owned by this panel.
      if (
        previousFocus?.isConnected &&
        (panel?.contains(document.activeElement) || document.activeElement === document.body)
      ) {
        previousFocus.focus({ preventScroll: true })
      }
    }
  }, [])

  useEffect(() => {
    if (isReady) panelSelection.current = selectedConversationId
  }, [isReady, selectedConversationId])

  const restorePreviousConversation = useCallback(() => {
    if (!ownsSelection.current) return
    ownsSelection.current = false
    dispatch((dispatch, getState) => {
      const state = getState()
      if (getSelectedClientId(state) !== clientId || getAuthUserId(state) !== providerId) return
      const currentId = getSelectedConversationId(state)
      // Do not overwrite a selection made outside this panel. A null selection is
      // also ours while the embedded messenger handles a rejected conversation.
      if (currentId !== null && currentId !== panelSelection.current) return
      dispatch(
        previousConversationId.current
          ? setSelectedConversation(previousConversationId.current)
          : clearSelectedConversation(),
      )
    })
  }, [clientId, dispatch, providerId])

  useEffect(() => {
    // Read after the outgoing panel's cleanup restores the surrounding selection.
    previousConversationId.current = dispatch((_, getState) => getSelectedConversationId(getState()))
    ownsSelection.current = true
    dispatch(clearSelectedConversation())
    dispatch(setPendingInitialThreadId(null))
    setIsReady(true)
    return restorePreviousConversation
  }, [dispatch, restorePreviousConversation])

  const handlePairUnavailable = useCallback(() => {
    restorePreviousConversation()
    onUnavailable()
  }, [onUnavailable, restorePreviousConversation])

  const handleEscape = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Escape' || event.nativeEvent.isComposing) return
    // Let dialogs rendered through a portal handle their own Escape.
    if (!event.currentTarget.contains(event.target as Node)) return
    event.stopPropagation()
    // DropdownButton exposes its open state on the listbox trigger; toggle it through its normal click handler.
    // Covered by the Escape assertions in Client.common.sidepanel.spec.ts: 'keeps the compact panel, long names,
    // header and composer inside the viewport'.
    const openMenu = event.currentTarget.querySelector<HTMLElement>('[aria-haspopup="listbox"][aria-expanded="true"]')
    if (openMenu) {
      event.preventDefault()
      openMenu.click()
      openMenu.focus()
    } else {
      onClose()
    }
  }

  return createPortal(
    <Panel
      theme={theme}
      ref={panelRef}
      open
      aria-label={formatMessage(
        { defaultMessage: 'Messages for {clientName}', description: 'Label for the client profile messaging panel' },
        { clientName },
      )}
      aria-modal={false}
      tabIndex={-1}
      data-testid={tID(`${testIDPrefix}-panel`)}
      onMouseDownCapture={onMouseDownCapture}
      onKeyDownCapture={(event) => {
        // React Native text inputs stop key events before they bubble to the panel.
        if (event.target instanceof HTMLElement && event.target.matches('input, textarea')) handleEscape(event)
      }}
      onKeyDown={handleEscape}
    >
      <MessengerContainer testID={tID(`${testIDPrefix}-messenger`)}>
        {isReady && isActiveMessenger && (
          <BaseMessenger
            compact
            compactDragHandle={<CompactMessengerDragHandle panelRef={panelRef} testIDPrefix={testIDPrefix} />}
            testIDPrefix={testIDPrefix}
            onClose={onClose}
            onExpandMessages={(thread, message) => {
              restorePreviousConversation()
              dispatch(setLastConversationClientId(clientId))
              dispatch(
                setMessageCenterTarget({
                  clientId,
                  providerId,
                  threadId: thread.id,
                  draftCounterpart:
                    !thread.conversationId && thread.recipientId === counterpart.participant_lyra_id
                      ? counterpart
                      : undefined,
                  message,
                }),
              )
              onClose()
              navigate(MESSENGER.route)
            }}
            initialProviderPairCounterpart={counterpart}
            onInitialProviderPairUnavailable={handlePairUnavailable}
            selectedClientId={clientId}
            isComposing={false}
            onSelectedClientIdChanged={noop}
            showEmptyView={false}
            onIsComposingChanged={noop}
            onOpenClientProfile={onClose}
            onOpenAssignment={(assignmentId) => {
              onClose()
              navigateToAssignment(clientId, assignmentId)
            }}
            onOpenNotes={(noteId) => {
              onClose()
              navigateToNote(clientId, noteId)
            }}
            initialDetailPanelOpen={false}
            entryPoint={ChatEntryPoint.CLIENT_HOME}
            messageToInsert={null}
            setMessageToInsert={noop}
          />
        )}
      </MessengerContainer>
    </Panel>,
    document.body,
  )
}
